import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Save, RefreshCw, Tag } from "lucide-react";

const fmt$ = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function UpsellCatalogAdminPage() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState({});
  const [prices, setPrices] = useState({}); // { [key]: string }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/change-orders/settings/upsell-catalog");
      setCatalog(res.data.catalog || {});
      // Seed prices from the current catalog values
      const initial = {};
      Object.values(res.data.catalog || {}).forEach(items =>
        items.forEach(it => {
          if (it.default_unit_price !== undefined)
            initial[it.key] = String(it.default_unit_price);
        })
      );
      setPrices(initial);
      setDirty(false);
    } catch {
      toast.error("Failed to load catalog");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (key, val) => {
    setPrices(p => ({ ...p, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(prices).forEach(([k, v]) => {
        const n = parseFloat(v);
        if (!isNaN(n) && n >= 0) payload[k] = n;
      });
      await apiClient.patch("/change-orders/settings/upsell-catalog", { prices: payload });
      toast.success("Rate card saved");
      setDirty(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const categories = Object.keys(catalog);

  return (
    <div className="min-h-screen bg-zinc-50/40">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 py-5 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/upsells")}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-zinc-600" />
            </button>
            <div className="w-9 h-9 rounded-lg bg-violet-600 text-white flex items-center justify-center shrink-0">
              <Tag className="w-4 h-4" />
            </div>
            <div>
              <h1 className="text-[19px] font-light tracking-tight text-zinc-900">Upsell Rate Card</h1>
              <p className="text-[12.5px] text-zinc-500 mt-0.5">Edit default unit prices for the upsell service catalog.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} className="h-8 text-[12px] border-zinc-200 gap-1.5" disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="h-8 text-[12px] bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6 max-w-4xl mx-auto space-y-6">
        {loading ? (
          <div className="text-center py-16 text-zinc-400 text-sm">Loading rate card…</div>
        ) : categories.length === 0 ? (
          <div className="text-center py-16 text-zinc-400 text-sm">No catalog items found.</div>
        ) : (
          categories.map(cat => {
            const items = catalog[cat] || [];
            return (
              <div key={cat} className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
                {/* Category header */}
                <div className="px-5 py-3.5 border-b border-zinc-100 bg-zinc-50/60">
                  <h2 className="text-[13px] font-semibold text-zinc-800 tracking-tight">{cat}</h2>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{items.length} item{items.length !== 1 ? "s" : ""}</p>
                </div>

                {/* Items table */}
                <div className="divide-y divide-zinc-100">
                  {/* Column headers */}
                  <div className="grid grid-cols-12 gap-3 px-5 py-2 bg-zinc-50/40">
                    <div className="col-span-5 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Item</div>
                    <div className="col-span-2 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Unit</div>
                    <div className="col-span-3 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Current Price</div>
                    <div className="col-span-2 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold text-right">New Price</div>
                  </div>

                  {items.map(it => {
                    const currentVal = prices[it.key] ?? "";
                    const current = parseFloat(currentVal);
                    return (
                      <div key={it.key} className="grid grid-cols-12 gap-3 items-center px-5 py-3 hover:bg-zinc-50/60 transition-colors">
                        <div className="col-span-5">
                          <div className="text-[13px] font-medium text-zinc-900">{it.label}</div>
                          <div className="text-[10.5px] text-zinc-400 font-mono mt-0.5">{it.key}</div>
                        </div>
                        <div className="col-span-2">
                          <span className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-medium">
                            {it.unit_type?.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="col-span-3 text-[13px] font-semibold tabular-nums text-zinc-700">
                          {it.default_unit_price !== undefined ? fmt$(it.default_unit_price) : <span className="text-zinc-400 italic text-[11px]">—</span>}
                        </div>
                        <div className="col-span-2">
                          <div className="relative">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11.5px] text-zinc-400 pointer-events-none">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="50"
                              value={currentVal}
                              onChange={(e) => handleChange(it.key, e.target.value)}
                              className="h-8 pl-5 text-[12.5px] tabular-nums text-right"
                              placeholder={it.default_unit_price ?? "0"}
                            />
                          </div>
                          {!isNaN(current) && current > 0 && it.default_unit_price !== undefined && current !== it.default_unit_price && (
                            <div className="text-[10px] text-violet-600 mt-0.5 text-right font-medium">
                              {current > it.default_unit_price ? "▲" : "▼"} {Math.abs(Math.round(((current - it.default_unit_price) / it.default_unit_price) * 100))}%
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}

        {dirty && (
          <div className="sticky bottom-4 flex justify-end">
            <div className="bg-white border border-zinc-200 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
              <span className="text-[12.5px] text-zinc-600">You have unsaved changes</span>
              <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={load}>Discard</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="h-8 text-[12px] bg-violet-600 hover:bg-violet-700 text-white gap-1.5">
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
